import { useCallback, useMemo, useRef, useState } from 'react';
import {
  isHierarchyNavigationKey,
  toFocusedIdAfterKey,
  toNodePositions,
  toParentIds,
  type NavigableNode,
} from './hierarchyNavigation';

/**
 * What a node has to carry to be reachable, nameable and selectable without a mouse.
 *
 * Spread onto the shape itself — the tile, the circle, the arc — which is what carries
 * `data-component-id`, the attribute `ViewPaneContainer` reads to decide what was selected.
 *
 * No `aria-selected`: nothing here knows what is selected, and these views have no visible
 * selected state either. It belongs with that.
 */
export type HierarchyNodeA11yProps = {
  role: 'treeitem';
  tabIndex: 0 | -1;
  'aria-level': number;
  'aria-setsize': number;
  'aria-posinset': number;
  'aria-label': string;
  /**
   * `true` on a node with a child on screen, absent on a leaf. Never `false`: absence is the
   * leaf state the tree pattern asks for, and nothing here collapses.
   */
  'aria-expanded'?: true;
  ref: (element: Element | null) => void;
  onKeyDown: (event: React.KeyboardEvent) => void;
  onFocus: () => void;
};

export type HierarchyNavigation = {
  /** Spread onto the element that owns the shapes: one tab stop, holding a tree. */
  treeProps: { role: 'tree'; 'aria-label': string };
  toNodeProps: (
    node: NavigableNode,
    accessibleName: string
  ) => HierarchyNodeA11yProps;
};

/**
 * One tab stop for the whole view, and the arrow keys inside it — the roving `tabIndex` the APG
 * tree pattern describes, since these views hold hundreds of nodes.
 *
 * Selection dispatches the click the mouse would have, so it reaches `ViewPaneContainer`'s
 * generic click-to-select rather than a second code path beside it.
 */
export const useHierarchyNavigation = (
  nodes: readonly NavigableNode[],
  treeLabel: string
): HierarchyNavigation => {
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const elements = useRef(new Map<string, Element>());

  const positions = useMemo(() => toNodePositions(nodes), [nodes]);
  const parentIds = useMemo(() => toParentIds(nodes), [nodes]);

  /* The remembered node while it is still drawn, and the first one otherwise. Changing the
     sizing field or the depth setting rebuilds the tree under the reader, and a `tabIndex` left
     on a node that no longer exists is a view with no tab stop at all. */
  const activeId =
    focusedId !== null && nodes.some(({ id }) => id === focusedId)
      ? focusedId
      : (nodes[0]?.id ?? null);

  const focus = useCallback((id: string) => {
    setFocusedId(id);
    /* `focus()` is on `HTMLElement` and, since SVG2, on `SVGElement` — but not on `Element`,
       which is the only type the two renderers have in common. */
    const element = elements.current.get(id);
    if (element instanceof HTMLElement || element instanceof SVGElement) {
      element.focus();
    }
  }, []);

  const toNodeProps = useCallback(
    (node: NavigableNode, accessibleName: string): HierarchyNodeA11yProps => {
      const { setSize, positionInSet } = positions.get(node.id) ?? {
        setSize: 1,
        positionInSet: 1,
      };
      return {
        role: 'treeitem',
        tabIndex: node.id === activeId ? 0 : -1,
        'aria-level': node.level,
        'aria-setsize': setSize,
        'aria-posinset': positionInSet,
        'aria-label': accessibleName,
        ...(parentIds.has(node.id) ? { 'aria-expanded': true as const } : {}),
        ref: (element: Element | null) => {
          if (element) {
            elements.current.set(node.id, element);
            return;
          }
          elements.current.delete(node.id);
        },
        onFocus: () => setFocusedId(node.id),
        onKeyDown: (event: React.KeyboardEvent) => {
          if (isHierarchyNavigationKey(event.key)) {
            const next = toFocusedIdAfterKey(nodes, node.id, event.key);
            /* Only once there is somewhere to go. Swallowing the key at the end of the list
               would stop the page scrolling for a reader who has run out of tree. */
            if (next === null) return;
            event.preventDefault();
            focus(next);
            return;
          }
          if (event.key !== 'Enter' && event.key !== ' ') return;
          /* Swallowed before the guard below, not after it. A group a rule built is not a
             component and there is nothing to select - but the focus is inside the tree, and
             letting Space through scrolls the pane out from under the reader. Every grouping
             rule except parent and parent-all builds those groups, so that is an ordinary level
             of an ordinary viewpoint rather than a corner. */
          event.preventDefault();
          if (node.modelId === null) return;
          event.currentTarget.dispatchEvent(
            new MouseEvent('click', { bubbles: true, cancelable: true })
          );
        },
      };
    },
    [activeId, focus, nodes, parentIds, positions]
  );

  return {
    treeProps: { role: 'tree', 'aria-label': treeLabel },
    toNodeProps,
  };
};
